import fromPairs from 'lodash/fromPairs';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import sum from 'lodash/sum';
import zip from 'lodash/zip';
import moment from 'moment';

import { ZALANDO_ACCOUNT_SF_ID } from '../accounts/constants';
import { MEDIA_BUSINESSES } from '../constants';
import { salesChannelsEnum } from '../opportunities/constants';
import { isInternal } from '../opportunities/utils/utils';
import { productLabel } from '../products/constants';
import { INTERNAL_DATE_FORMAT } from '../utils/constants';
import {
  allStatusesEnum as invoiceStatuses,
  bookingEntryTypes,
} from './constants';

export function pickWithDefault(object, paths, defaultValue) {
  const entries = paths.map(path => get(object, path, defaultValue));
  return fromPairs(zip(paths, entries));
}

export function getMonthRange(value) {
  if (!value) return [null, null];

  const startDate = moment(value).startOf('month');
  const endDate = startDate.clone().endOf('month');

  return [startDate, endDate].map(date => date.format(INTERNAL_DATE_FORMAT));
}

export function getLastMonthRange(value) {
  if (!value) return [null, null];

  const startDate = moment(value).subtract(1, 'months').startOf('month');
  const endDate = moment(value);

  return [startDate, endDate].map(date => date.format(INTERNAL_DATE_FORMAT));
}

export function isInvoiceStatusReadOnly(status) {
  // TODO check permissions of current user
  return ![
    invoiceStatuses.DRAFT.value,
    invoiceStatuses.CHANGES_REQUESTED.value,
  ].includes(status);
}

export function hasSapFieldsValue(invoice) {
  const {
    sapStatus,
    sapPaidAt,
    sapOrderNumber,
    sapInvoiceNumbers,
    sapReversalInvoiceNumbers,
  } = invoice;
  if (
    sapStatus ||
    sapPaidAt ||
    sapOrderNumber ||
    sapReversalInvoiceNumbers.length ||
    sapInvoiceNumbers.length
  )
    return true;
  return false;
}

export function isInvoiceStatusExported(status) {
  return status === invoiceStatuses.EXPORTED.value;
}

export function isInvoiceStatusExportedOrCanceled(status) {
  return [
    invoiceStatuses.EXPORTED.value,
    invoiceStatuses.CANCELED.value,
  ].includes(status);
}

export function isInvoiceStatusNotCanceled(status) {
  return status !== invoiceStatuses.CANCELED.value;
}

export function groupEntries(bookingEntries, products, productBudgets = {}) {
  const sortedEntries = orderBy(bookingEntries, [
    entry => entry.productId || 0,
    'serviceStartDate',
    'serviceEndDate',
  ]);
  const getEntryProductLabel = entry =>
    productLabel(products.find(each => each.id === entry.productId));
  const groups = groupBy(sortedEntries, getEntryProductLabel);
  return Object.entries(groups).map(([label, entries]) => {
    const productId = entries[0].productId || 0;
    return {
      label,
      productId,
      budget: productBudgets[productId],
      entries,
    };
  });
}

function getCoInvestCampaignLines(campaign) {
  return campaign.campaignLines.filter(({ coInvest }) => coInvest > 0);
}

function getNonMediaCampaignLines(campaign, products) {
  return campaign.campaignLines.filter(line => {
    const product = products.find(({ id }) => id === line.product.id);
    return !MEDIA_BUSINESSES.includes(product.business);
  });
}

export function maxDate(date1, date2) {
  if (date1 && date2) {
    return moment(date1).isAfter(date2) ? date1 : date2;
  }

  return date1 ?? date2;
}

export function minDate(date1, date2) {
  if (date1 && date2) {
    return moment(date1).isBefore(date2) ? date1 : date2;
  }

  return date1 ?? date2;
}

export function generateBookingEntriesFromCampaignLines(
  campaign,
  products,
  entryType = bookingEntryTypes.NON_MEDIA.value,
) {
  const isCoInvest = entryType === bookingEntryTypes.CO_INVEST.value;

  const campaignLines = isCoInvest
    ? getCoInvestCampaignLines(campaign)
    : getNonMediaCampaignLines(campaign, products);

  const bookingEntryType = isCoInvest
    ? bookingEntryTypes.CO_INVEST.label
    : bookingEntryTypes.NON_MEDIA.label;

  const budgetField = isCoInvest ? 'coInvest' : 'netClientBudget';

  const opportunityId = campaign.opportunity.id;

  const campaignLineGroups = Object.values(
    groupBy(campaignLines, 'product.id'),
  );

  return campaignLineGroups.map(lines =>
    lines.reduce(
      (agg, line) => ({
        ...agg,
        debitAmount: agg.debitAmount + line[budgetField],
        serviceStartDate: minDate(
          agg.serviceStartDate,
          line.startDate || campaign.startDateIs || campaign.startDatePlan,
        ),
        serviceEndDate: maxDate(
          agg.serviceEndDate,
          line.endDate || campaign.endDateIs || campaign.endDatePlan,
        ),
      }),
      {
        debitAmount: 0,
        discount: 0,
        entryType: bookingEntryType,
        opportunityId,
        productId: lines[0].product.id, // all lines in a group have the same product ID
      },
    ),
  );
}

export function getAccountSfIdForInvoice(opportunity) {
  return isInternal(opportunity)
    ? ZALANDO_ACCOUNT_SF_ID
    : get(opportunity, 'accountSfId');
}

const EXTERNAL_BILLING_TYPE = 'External';
const INTERNAL_BILLING_TYPE = 'Internal';

export function getInvoiceBillingType(opportunity, accountSfId) {
  return opportunity.billingType === EXTERNAL_BILLING_TYPE &&
    accountSfId === ZALANDO_ACCOUNT_SF_ID
    ? INTERNAL_BILLING_TYPE
    : EXTERNAL_BILLING_TYPE;
}

export function aggregateCampaignBudgets(campaign) {
  if (!campaign) return { total: 0 };

  const budgets = {};
  campaign.campaignLines.forEach(line => {
    const productId = line.product.id;
    budgets[productId] = (budgets[productId] ?? 0) + line.netClientBudget;
  });
  budgets.total = sum(Object.values(budgets));
  return budgets;
}

export function isInvoiceApprovable(invoice) {
  const isSelfServiceInvoice =
    invoice.salesChannel === salesChannelsEnum.SELF_SERVICE;
  return isSelfServiceInvoice ? true : !isEmpty(invoice.accountSfId);
}
